<template>
  <iColumn
    gap="standard"
    width="fill"
    wrap="nowrap"
    data-test="call-roster-panel"
  >
    <iRow v-if="inviteLink" width="fill">
      <iButton
        variant="primary"
        size="small"
        icon-left="ic:baseline-person-add-alt-1"
        data-test="call-roster-add-people"
        @click="isInviteOpen = !isInviteOpen"
      >
        Add people
      </iButton>
    </iRow>

    <iColumn
      v-if="inviteLink && isInviteOpen"
      gap="extraSmall"
      width="fill"
      wrap="nowrap"
    >
      <iTextInput
        :model-value="inviteLink"
        label="Share this link"
        readonly
        width="fill"
        right-icon="copy"
        data-test="call-roster-invite-link"
        @click-icon-right="copyInviteLink"
      />
      <iText
        v-if="copiedInviteLink"
        font-size="small"
        variant="success"
        data-test="call-roster-invite-copied"
      >
        Link copied
      </iText>
    </iColumn>

    <iTextInput
      v-model="searchText"
      placeholder="Search for people"
      left-icon="ic:baseline-search"
      width="fill"
      data-test="call-roster-search"
    />

    <iColumn gap="small" width="fill" wrap="nowrap">
      <iText
        font-size="verySmall"
        font-weight="dark"
        variant="subtle"
        text-case="uppercase"
      >
        {{ isBroadcast ? "Watching" : "In the meeting" }}
      </iText>

      <iColumn
        gap="none"
        width="fill"
        wrap="nowrap"
        data-test="call-roster-group"
        :style-overrides="{borderWidth: 'thin', borderColor: 'light', borderRadius: 'large'}"
      >
        <iRow
          clickable
          gap="small"
          vertical-align="middle"
          wrap="nowrap"
          width="fill"
          data-test="call-roster-group-toggle"
          :aria-expanded="String(isGroupOpen)"
          :style-overrides="{paddingTop: 'small', paddingBottom: 'small', paddingLeft: 'standard', paddingRight: 'standard'}"
          @click="isGroupOpen = !isGroupOpen"
        >
          <iText width="fill" font-weight="dark" font-size="small">
            {{ isBroadcast ? "Viewers" : "Contributors" }}
          </iText>
          <iText font-size="small" data-test="call-roster-count">
            {{ entries.length }}
          </iText>
          <iIcon
            :name="isGroupOpen ? 'ic:baseline-keyboard-arrow-up' : 'ic:baseline-keyboard-arrow-down'"
            size="small"
            disable-hover
          />
        </iRow>

        <template v-if="isGroupOpen">
          <iDivider color="light" />
          <iText
            v-if="!filteredEntries.length"
            variant="subtle"
            font-size="small"
            data-test="call-roster-empty"
            :style-overrides="{paddingTop: 'small', paddingBottom: 'small', paddingLeft: 'standard'}"
          >
            No one matches "{{ searchText }}".
          </iText>

          <iRow
            v-for="entry in filteredEntries"
            :key="entry.id"
            gap="standard"
            align="left"
            vertical-align="middle"
            width="fill"
            wrap="nowrap"
            :data-test="`call-roster-row-${entry.id}`"
            :style-overrides="{
              paddingTop: 'small',
              paddingBottom: 'small',
              paddingLeft: 'standard',
              paddingRight: 'small',
            }"
          >
            <iAvatar :name="entry.displayName" size="large" />

            <iColumn width="fill" gap="none" :min-width="0">
              <iText ellipsis font-size="small">
                {{ entry.displayName }}{{ entry.isLocal ? " (You)" : "" }}
              </iText>
              <iText font-size="verySmall" variant="subtle" ellipsis>
                {{ roleLabel(entry) }}
              </iText>
            </iColumn>

            <iRow
              gap="small"
              width="hug"
              vertical-align="middle"
              wrap="nowrap"
            >
              <iIcon
                v-if="entry.handRaised"
                name="material-symbols:front-hand-outline-rounded"
                size="small"
                variant="warning"
                disable-hover
                :data-test="`call-roster-hand-${entry.id}`"
              />
              <iIcon
                v-if="entry.isVideoMuted"
                name="ic:baseline-videocam-off"
                size="small"
                variant="subtle"
                disable-hover
                :data-test="`call-roster-camera-off-${entry.id}`"
              />
              <iIcon
                v-if="entry.isAudioMuted"
                name="ic:baseline-mic-off"
                size="small"
                variant="subtle"
                disable-hover
                :data-test="`call-roster-muted-${entry.id}`"
              />
              <iIcon
                v-else-if="isSpeaking(entry.id)"
                name="ic:baseline-graphic-eq"
                size="small"
                variant="branded"
                disable-hover
                :data-test="`call-roster-speaking-${entry.id}`"
              />

              <iDropdown
                v-if="actionsFor(entry).length"
                :show-dropdown="openActionsFor === entry.id"
                overlay-position="bottom-right"
                :z-index="2200"
                @close="openActionsFor = null"
              >
                <template #trigger>
                  <iIcon
                    name="ic:baseline-more-vert"
                    size="small"
                    :tooltip="`More actions for ${entry.displayName}`"
                    :data-test="`call-roster-actions-${entry.id}`"
                    @click="toggleActions(entry.id)"
                  />
                </template>
                <template #overlay>
                  <iColumn
                    gap="none"
                    wrap="nowrap"
                    :min-width="200"
                    :style-overrides="{paddingTop: 'extraSmall', paddingBottom: 'extraSmall'}"
                  >
                    <iRow
                      v-for="action in actionsFor(entry)"
                      :key="action.id"
                      clickable
                      gap="small"
                      vertical-align="middle"
                      wrap="nowrap"
                      width="fill"
                      :data-test="`call-roster-${action.id}-${entry.id}`"
                      :style-overrides="{paddingTop: 'small', paddingBottom: 'small', paddingLeft: 'standard', paddingRight: 'large'}"
                      @click="runAction(action)"
                    >
                      <iIcon :name="action.icon" size="small" disable-hover />
                      <iText font-size="small" :wrap="false">
                        {{ action.label }}
                      </iText>
                    </iRow>
                  </iColumn>
                </template>
              </iDropdown>
            </iRow>
          </iRow>
        </template>
      </iColumn>
    </iColumn>

    <iColumn
      v-if="canModerate && !isBroadcast"
      gap="none"
      width="fill"
      wrap="nowrap"
      data-test="call-roster-room-controls"
      :style-overrides="{borderWidth: 'thin', borderColor: 'light', borderRadius: 'large'}"
    >
      <iText
        font-weight="dark"
        font-size="small"
        :style-overrides="{paddingTop: 'small', paddingBottom: 'small', paddingLeft: 'standard', paddingRight: 'standard'}"
      >
        Host controls
      </iText>
      <iDivider color="light" />
      <iRow
        v-for="control in roomControls"
        :key="control.id"
        gap="standard"
        vertical-align="middle"
        wrap="nowrap"
        width="fill"
        :data-test="`call-roster-${control.id}-row`"
        :style-overrides="{paddingTop: 'small', paddingBottom: 'small', paddingLeft: 'standard', paddingRight: 'standard'}"
      >
        <iColumn gap="none" width="fill" :min-width="0">
          <iText font-size="small">
            {{ control.label }}
          </iText>
          <iText font-size="verySmall" variant="subtle">
            {{ control.description }}
          </iText>
        </iColumn>
        <iSwitch
          size="small"
          :model-value="control.isOn"
          :aria-label="control.label"
          :data-test="`call-roster-${control.id}-toggle`"
          @update:model-value="setRoomControl(control.id, $event)"
        />
      </iRow>
      <iColumn
        v-if="roomState.waitingRoomEnabled || waitingGuests.length"
        gap="small"
        width="fill"
        wrap="nowrap"
        data-test="call-roster-waiting-list"
        :style-overrides="{paddingTop: 'small', paddingBottom: 'standard', paddingLeft: 'standard', paddingRight: 'standard'}"
      >
        <iDivider color="light" />
        <iRow
          gap="small"
          vertical-align="middle"
          wrap="nowrap"
          width="fill"
        >
          <iText
            font-size="verySmall"
            font-weight="dark"
            variant="subtle"
            text-case="uppercase"
          >
            Waiting to join
          </iText>
          <iText font-size="verySmall" variant="subtle">
            {{ roomState.waiting.length + waitingGuests.length }}
          </iText>
        </iRow>
        <iText
          v-if="!roomState.waiting.length && !waitingGuests.length"
          variant="subtle"
          font-size="small"
          data-test="call-roster-waiting-empty"
        >
          No one is waiting.
        </iText>
        <iRow
          v-for="waitingUser in roomState.waiting"
          :key="waitingUser.userId"
          gap="small"
          align="left"
          vertical-align="middle"
          width="fill"
          wrap="nowrap"
          :data-test="`call-roster-waiting-${waitingUser.userId}`"
        >
          <iAvatar :name="waitingUser.displayName" :src="waitingUser.avatarUrl" size="large" />
          <iColumn width="fill" gap="none" :min-width="0">
            <iText ellipsis>
              {{ waitingUser.displayName }}
            </iText>
          </iColumn>
          <iButton
            variant="text"
            size="small"
            :data-test="`call-roster-deny-${waitingUser.userId}`"
            @click="$emit('deny-user', waitingUser.userId)"
          >
            Deny
          </iButton>
          <iButton
            variant="secondary"
            size="small"
            :data-test="`call-roster-admit-${waitingUser.userId}`"
            @click="$emit('admit-user', waitingUser.userId)"
          >
            Admit
          </iButton>
        </iRow>
        <iRow
          v-for="waitingGuest in waitingGuests"
          :key="`guest-${waitingGuest.guestId}`"
          gap="small"
          align="left"
          vertical-align="middle"
          width="fill"
          wrap="nowrap"
          :data-test="`call-roster-waiting-guest-${waitingGuest.guestId}`"
        >
          <iAvatar :name="waitingGuest.displayName" size="large" />
          <iColumn width="fill" gap="none" :min-width="0">
            <iText ellipsis>
              {{ waitingGuest.displayName }}
            </iText>
            <iText font-size="verySmall" variant="subtle">
              Guest
            </iText>
          </iColumn>
          <iButton
            variant="text"
            size="small"
            :data-test="`call-roster-deny-guest-${waitingGuest.guestId}`"
            @click="$emit('deny-guest', waitingGuest.guestId)"
          >
            Deny
          </iButton>
          <iButton
            variant="secondary"
            size="small"
            :data-test="`call-roster-admit-guest-${waitingGuest.guestId}`"
            @click="$emit('admit-guest', waitingGuest.guestId)"
          >
            Admit
          </iButton>
        </iRow>
      </iColumn>
    </iColumn>

    <iColumn
      v-if="canModerate"
      gap="extraSmall"
      width="fill"
      align="center"
    >
      <iCallCta
        variant="danger"
        block
        data-test="call-roster-end-for-everyone"
        @click="$emit('end-for-everyone')"
      >
        End the call for everyone
      </iCallCta>
      <iText font-size="verySmall" variant="subtle" text-align="center">
        Everyone is disconnected and the room closes.
      </iText>
    </iColumn>
  </iColumn>
</template>

<script>
export default {
  name: "CallRosterPanel",
  props: {
    entries: {
      type: Array,
      default: () => [],
    },
    canModerate: {
      type: Boolean,
      default: false,
    },
    speakingPeerIds: {
      type: Array,
      default: () => [],
    },
    isBroadcast: {
      type: Boolean,
      default: false,
    },
    roomState: {
      type: Object,
      default: () => ({ locked: false, waitingRoomEnabled: false, waiting: [] }),
    },
    inviteLink: {
      type: String,
      default: "",
    },
  },
  emits: [
    "lower-hand",
    "mute-participant",
    "request-unmute",
    "remove-participant",
    "update-room-state",
    "admit-user",
    "deny-user",
    "admit-guest",
    "deny-guest",
    "end-for-everyone",
  ],
  data() {
    return {
      searchText: "",
      copiedInviteLink: false,
      isInviteOpen: false,
      isGroupOpen: true,
      openActionsFor: null,
    };
  },
  computed: {
    roomControls() {
      return [
        { id: "lock", label: "Lock the meeting", description: "No one else can join while it's locked.", isOn: Boolean(this.roomState.locked) },
        { id: "waiting", label: "Waiting room", description: "New people wait until you let them in.", isOn: Boolean(this.roomState.waitingRoomEnabled) },
      ];
    },
    waitingGuests() {
      return this.roomState.waitingGuests || [];
    },
    filteredEntries() {
      const needle = this.searchText.trim().toLowerCase();
      if (!needle) {
        return this.entries;
      }

      return this.entries.filter(entry => (entry.displayName || "").toLowerCase().includes(needle));
    },
  },
  methods: {
    setRoomControl(controlId, isOn) {
      const next = { locked: Boolean(this.roomState.locked), waitingRoomEnabled: Boolean(this.roomState.waitingRoomEnabled) };
      if (controlId === "lock") {
        next.locked = isOn;
      } else if (controlId === "waiting") {
        next.waitingRoomEnabled = isOn;
      } else {
        throw new Error(`CallRosterPanel: unknown room control ${JSON.stringify(controlId)}; expected lock or waiting`);
      }
      this.$emit("update-room-state", next);
    },
    copyInviteLink() {
      if (!navigator.clipboard || !window.isSecureContext) {
        return;
      }
      navigator.clipboard.writeText(this.inviteLink).then(() => {
        this.copiedInviteLink = true;
      });
    },
    toggleActions(entryId) {
      this.openActionsFor = this.openActionsFor === entryId ? null : entryId;
    },
    runAction(action) {
      this.openActionsFor = null;
      action.run();
    },
    actionsFor(entry) {
      if (!this.canModerate || entry.isLocal) {
        return [];
      }
      const actions = [];
      if (entry.handRaised) {
        actions.push({ id: "lower-hand", icon: "material-symbols:front-hand-outline-rounded", label: "Lower hand", run: () => this.$emit("lower-hand", { identity: entry.identity }) });
      }
      if (!entry.isViewer && !entry.isAudioMuted) {
        actions.push({ id: "mute", icon: "ic:baseline-mic-off", label: "Mute for everyone", run: () => this.$emit("mute-participant", { identity: entry.identity, trackSid: entry.audioTrackSid }) });
      }
      if (!entry.isViewer && entry.isAudioMuted) {
        actions.push({ id: "ask-unmute", icon: "ic:baseline-mic", label: "Ask to unmute", run: () => this.$emit("request-unmute", { identity: entry.identity }) });
      }
      actions.push({ id: "remove", icon: "ic:baseline-person-remove", label: "Remove from the call", run: () => this.$emit("remove-participant", { identity: entry.identity, displayName: entry.displayName }) });

      return actions;
    },
    isSpeaking(peerId) {
      return this.speakingPeerIds.includes(peerId);
    },
    roleLabel(entry) {
      if (entry.isLocal) {
        return this.canModerate ? "Meeting host" : "Participant";
      }
      if (entry.isViewer) {
        return "Watching";
      }
      if (this.isBroadcast) {
        return this.isSpeaking(entry.id) ? "Host · Speaking" : "Host";
      }

      return this.isSpeaking(entry.id) ? "Speaking" : "Participant";
    },
  },
};
</script>
